import { FC, useRef, useMemo, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { observer } from 'mobx-react-lite';
import { useMutation } from '@tanstack/react-query';
import { useStore } from '@hooks/useStore';
import { useMyTranslations } from '@hooks/useMyTranslations';
import { Button } from '@components/Core/Button';
import { IUserProfile } from '@stores/profile/types';
import {
  VerificationCodeFormFileds,
  VerificationCodeFormTransFields,
  IVerificationCodeFormProps,
} from './types';
import { verificationCodeFormTranslations } from './translations';
import {
  GMAIL_LINK,
  MAILTO_LINK,
  OUTLOOK_LINK,
  PASS_LENGTH,
  YAHOO_LINK,
  DEFAULT_VAL,
} from './constants';

const VerificationCodeForm: FC<IVerificationCodeFormProps> = ({ onSubmit }) => {
  const {
    authStore: { authData, sendCreds, verifyCode, setAuthStep },
    alertStore: { showAlert },
  } = useStore();

  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  const { mutate: resendCode, isPending: isPendingSendAgain } = useMutation({
    mutationFn: sendCreds,
    mutationKey: ['sendCreds'],
    onSuccess: () => {
      showAlert('Code has been sent again', 'success');
    },
  });

  const { mutate, isPending } = useMutation({
    mutationFn: verifyCode,
    mutationKey: ['verifyCode'],
    onSuccess: (res: IUserProfile | undefined) => {
      if (res && !res.isAgreedPolicy) setAuthStep('finish');
      else setAuthStep('creds');
    },
  });

  const useFormMethods = useForm<VerificationCodeFormFileds>({
    defaultValues: { code: DEFAULT_VAL },
  });

  const { handleSubmit, setValue, watch, control, reset } = useFormMethods;

  const onSubmitHandler = useCallback(
    ({ code }: VerificationCodeFormFileds) => {
      if (onSubmit) {
        onSubmit(code);
        reset();
      } else mutate(code);
    },
    [onSubmit]
  );

  const { t } = useMyTranslations<VerificationCodeFormTransFields>([
    verificationCodeFormTranslations.name,
  ]);

  const handleChange = useCallback(
    (val: string, index: number) => {
      // TODO make request after all inputs are filled?
      if (val.length === PASS_LENGTH && !isNaN(Number(val))) {
        // If the input is filled with PASS_LENGTH characters, we set the value directly
        // for copy-paste functionality
        setValue('code', val);
        inputsRef.current[index]?.blur();
        return;
      }
      const value = val.length > 1 ? val[1] : val;
      const code = watch('code')
        .padEnd(PASS_LENGTH, ' ')
        .replace(/./g, (item, i) => (i === index ? value || ' ' : item));
      setValue('code', code);
      if (value && index < 3) {
        inputsRef.current[index + 1]?.focus();
      }
    },
    [inputsRef.current]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
      if (
        (e.key === 'Backspace' || e.key === 'Delete') &&
        index > 0 &&
        !watch('code')[index].trim()
      ) {
        inputsRef.current[index - 1]?.focus();
      }
    },
    [inputsRef.current]
  );

  const onSupportClick = useCallback(() => {
    if (!authData) return;
    const email = authData.email.toLowerCase();
    if (email.endsWith('@gmail.com')) {
      window.open(GMAIL_LINK, '_blank');
    } else if (
      email.endsWith('@outlook.com') ||
      email.endsWith('@hotmail.com') ||
      email.endsWith('@live.com') ||
      email.endsWith('@msn.com')
    ) {
      window.open(OUTLOOK_LINK, '_blank');
    } else if (email.endsWith('@yahoo.com') || email.endsWith('@ymail.com')) {
      window.open(YAHOO_LINK, '_blank');
    } else {
      window.location.href = MAILTO_LINK;
    }
  }, [authData]);

  const isDisabled = useMemo(() => {
    return (
      watch('code').includes(' ') ||
      watch('code').length < PASS_LENGTH ||
      isPendingSendAgain ||
      isPending
    );
  }, [isPendingSendAgain, isPending, watch('code')]);

  return authData ? (
    <div className="relative w-full flex flex-col justify-center items-center gap-y-[24px]">
      <div className="w-[200px]">
        <p className="text-center small-text">{t('title')}</p>
      </div>
      <div className="flex justify-center items-center gap-x-[8px]">
        <p className="text-center small-text text-primary">{authData.email}</p>
        {/* <EditIcon width={16} height={16} /> */}
      </div>
      <form className="flex items-center gap-4">
        {/* use ts-ignore due to Type instantiation is excessively deep and possibly infinite.ts(2589) error */}
        {/* @ts-ignore */}
        <Controller
          name="code"
          control={control}
          render={() => (
            <>
              {Array.from({ length: PASS_LENGTH }, (_, i) => i).map((i) => (
                <input
                  key={i}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  // value={watch('code')[i] || ''}
                  value={watch('code')[i] === ' ' ? '' : watch('code')[i] || ''}
                  onChange={(e) => handleChange(e.target.value.replace(/\D/, ''), i)}
                  onKeyDown={(e) => handleKeyDown(e, i)}
                  onFocus={() => {
                    const el = inputsRef.current[i];
                    if (watch('code')[i] && el) {
                      // use redefine input type due to 'The input element's type ('number') does not support selection.'
                      el.type = 'text';
                      el.setSelectionRange(1, 1);
                      el.type = 'number';
                    }
                  }}
                  ref={(el) => el !== null && (inputsRef.current[i] = el)}
                  className="w-8 text-center border-b-2 border-gray-300 focus:border-blue-500 outline-none text-lg"
                />
              ))}
            </>
          )}
        />
      </form>
      <div className="h-[48px] flex flex-col justify-between">
        <div className="flex-centred gap-x-[8px] ">
          <p className="small-text">{t('question')}</p>
          <button
            type="button"
            className="small-text text-primary"
            onClick={() => resendCode({ email: authData.email })}
            disabled={isPendingSendAgain || isPending}
          >
            {t('SendAgain')}
          </button>
        </div>
        <div className="flex-centred gap-x-[8px]">
          <p className="small-text">{t('or')}</p>
          <button className="flex justify-center gap-x-1" onClick={onSupportClick}>
            <p className="small-text text-primary">{t('support')}</p>
          </button>
        </div>
      </div>
      <Button onClick={handleSubmit(onSubmitHandler)} text={t('Confirm')} disabled={isDisabled} />
    </div>
  ) : null;
};

export default observer(VerificationCodeForm);
